import type { InstallmentOption } from '@/features/quotes/components/premium-card.component';
import type { PaymentInfo, Proposal } from '@/services/proposals/types';
import { formatCurrency } from '@/shared/utils';

function getBlockInstallmentCount(block: PaymentInfo): number {
  return block.installmentPeriodCount > 0
    ? block.installmentPeriodCount
    : (block.installmentList?.length ?? 0);
}

function getBlockTotal(block: PaymentInfo): number {
  return (block.installmentList ?? []).reduce(
    (sum, installment) => sum + (installment.installmentAmount ?? 0),
    0
  );
}

function formatInterestPercentage(percentage: number): string {
  return `${percentage.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
}

/**
 * Deriva, a partir do `paymentInfo` da proposta, a lista de opções de
 * parcelamento exibida no tooltip do PremiumCard. Cada bloco do `paymentInfo`
 * é uma opção (à vista, 1+1, 1+2, ...), ordenada pelo nº de parcelas. O
 * percentual de juros é calculado em relação ao total à vista (menor nº de
 * parcelas); quando os totais são iguais, a opção é "Sem Juros".
 */
export function getInstallmentOptions(
  proposal: Proposal | undefined
): InstallmentOption[] {
  const blocks = proposal?.paymentInfo;
  if (!blocks?.length) return [];

  const sortedBlocks = [...blocks]
    .filter(block => getBlockInstallmentCount(block) > 0)
    .sort((a, b) => getBlockInstallmentCount(a) - getBlockInstallmentCount(b));
  if (!sortedBlocks.length) return [];

  const cashTotal = getBlockTotal(sortedBlocks[0]);
  const seenCounts = new Set<number>();

  return sortedBlocks.reduce<InstallmentOption[]>((options, block) => {
    const count = getBlockInstallmentCount(block);
    if (seenCounts.has(count)) return options;
    seenCounts.add(count);

    const total = getBlockTotal(block);
    const installmentValue = count > 0 ? total / count : total;
    const interestPercentage =
      cashTotal > 0 ? (total / cashTotal - 1) * 100 : 0;
    const hasInterest = interestPercentage > 0.01;

    options.push({
      label: count <= 1 ? 'À vista' : `1+${count - 1}`,
      interestLabel: hasInterest
        ? formatInterestPercentage(interestPercentage)
        : 'Sem Juros',
      installmentValue: formatCurrency(installmentValue),
      total: formatCurrency(total),
    });
    return options;
  }, []);
}
